import {SoiPeeringWan, TrunkingSite} from "../../app/types";
import {FormSection} from "../../common/components/SectionedForm";
import {useTranslation} from "react-i18next";
import React, {ReactElement, ReactNode, useCallback} from "react";
import {api} from "../../common/api";
import {useFetchData} from "../../common/servercall/useFetchData";
import {LabelValues, LabelValueType} from "../../common/components/LabelValues";
import PaperStack from "../../common/form/PaperStack";
import FormGroup from "../../common/form/FormGroup";
import LanIcon from '@mui/icons-material/Lan';
import FormButtons from "../../common/form/FormButtons";
import SpinnableSubmitButton from "../../common/form/SpinnableSubmitButton";
import SoiPeeringWanFormScreen from "./WanForm";
import axios from "axios";

export const useSoiPeeringWanSection = (trunkingSite: TrunkingSite | undefined) : FormSection<SoiPeeringWan> => {
    const { t } = useTranslation()

    const fetchConfig = useCallback(
        async (productId: string | undefined): Promise<SoiPeeringWan | undefined> => {
            if (!productId) return
            const { data } = await api.get<SoiPeeringWan>(`/api/trunking/site/${productId}/soi-peering-wan`)
            return data
        },
        []
    )
    const { data, isFetching, error, refetch } = useFetchData<SoiPeeringWan>(
        useCallback(() => fetchConfig(trunkingSite?.productId), [fetchConfig, trunkingSite?.productId])
    )

    const generalLabelValues = useCallback(
        (value: SoiPeeringWan): LabelValueType[] => {
            return [
                { label: t('trunking-sites.pbx-wan-ip'), value: <>{value.pbxWanIp}</> },
                { label: t('trunking-sites.pbx-wan-port'), value: <>{value.pbxWanPort}</> },
            ]}, [t]
    )

    const renderSoiWan = useCallback(
        (soiPeeringWan: SoiPeeringWan): ReactNode => {
            return (
                <PaperStack>
                    <FormGroup key={1} fullWidth={true}>
                        <LabelValues labelValues={generalLabelValues(soiPeeringWan)} />
                    </FormGroup>
                    <></>
                </PaperStack>
            )
        },
        [generalLabelValues]
    )

    const onGeneralSubmit = useCallback((onSucceeded: () => void) =>
            async (formData: SoiPeeringWan) => {
                await api.put(`/api/trunking/site/${trunkingSite?.productId}/soi-peering-wan`, formData)
                await refetch()
                onSucceeded();
            },
        [refetch, trunkingSite?.productId],
    )

    const buildFormButtons = useCallback( (cancelEditButton: ReactElement) => (isSubmitting: boolean) =>
            <FormButtons buttons={[<SpinnableSubmitButton label={t('general.form-update')} showSpinner={isSubmitting} />, cancelEditButton]} fullWidth={true}/>
        , [t])

    const renderForm = useCallback((soiPeeringWan: SoiPeeringWan, cancelEditButton: ReactElement, onSucceeded: () => void): ReactNode =>
        <SoiPeeringWanFormScreen form={soiPeeringWan} buildFormButtons={buildFormButtons(cancelEditButton)}
                                 onSubmit={onGeneralSubmit(onSucceeded)} />, [onGeneralSubmit, buildFormButtons])
    return {
        title: t('trunking-sites.section.wan'),
        data: data,
        isFetching: isFetching,
        error: !!error ?axios.isAxiosError(error) && error.response?.status === 404? `trunking-sites.no-trunk-defined` : error : null,
        refetch: refetch,
        renderDetails: renderSoiWan,
        renderForm: renderForm,
        enabled: () => true,
        icon: () => <LanIcon />
    }
}